import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../../lib/api';

type SignupInput = {
  legalName: string;
  taxNumber: string;
  fullName: string;
  email: string;
  password: string;
};

export function SignupPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const signupSchema = useMemo(
    () =>
      z.object({
        legalName: z.string().min(2, t('signup.validation.legalNameMin')),
        taxNumber: z.string().regex(/^\d{10}$/, t('signup.validation.taxNumberFormat')),
        fullName: z.string().min(2, t('signup.validation.fullNameMin')),
        email: z.string().email(t('signup.validation.emailInvalid')),
        password: z.string().min(12, t('signup.validation.passwordMin')),
      }),
    [t],
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignupInput>({ resolver: zodResolver(signupSchema) });

  async function onSubmit(input: SignupInput) {
    setSubmitting(true);
    try {
      await api.post('/auth/signup', input);
      toast.success(t('signup.toast.success'));
      navigate('/eposta-dogrula', { state: { email: input.email } });
    } catch (err) {
      const message = err instanceof ApiError ? err.message : t('signup.toast.error');
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-custel-bg-subtle p-8">
      <div className="w-full max-w-lg rounded-custel border border-custel-border bg-custel-surface p-8 shadow-sm">
        <h1 className="mb-2 text-2xl font-semibold text-custel-text">{t('signup.title')}</h1>
        <p className="mb-6 text-sm text-custel-text-secondary">{t('signup.subtitle')}</p>

        <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
          <fieldset className="space-y-4">
            <legend className="mb-2 text-sm font-semibold uppercase text-custel-text-secondary">
              {t('signup.section.company')}
            </legend>

            <div>
              <label className="mb-1 block text-sm font-medium text-custel-text" htmlFor="legalName">
                {t('signup.field.legalName')}
              </label>
              <input
                id="legalName"
                type="text"
                className="input-base"
                placeholder={t('signup.field.legalNamePlaceholder')}
                {...register('legalName')}
              />
              {errors.legalName && <p className="mt-1 text-xs text-custel-danger">{errors.legalName.message}</p>}
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-custel-text" htmlFor="taxNumber">
                {t('signup.field.taxNumber')}
              </label>
              <input
                id="taxNumber"
                type="text"
                inputMode="numeric"
                maxLength={10}
                className="input-base font-mono"
                placeholder="1234567890"
                {...register('taxNumber')}
              />
              {errors.taxNumber && <p className="mt-1 text-xs text-custel-danger">{errors.taxNumber.message}</p>}
            </div>
          </fieldset>

          <fieldset className="space-y-4 pt-2">
            <legend className="mb-2 text-sm font-semibold uppercase text-custel-text-secondary">
              {t('signup.section.adminAccount')}
            </legend>

            <div>
              <label className="mb-1 block text-sm font-medium text-custel-text" htmlFor="fullName">
                {t('signup.field.fullName')}
              </label>
              <input id="fullName" type="text" className="input-base" {...register('fullName')} />
              {errors.fullName && <p className="mt-1 text-xs text-custel-danger">{errors.fullName.message}</p>}
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-custel-text" htmlFor="email">
                {t('signup.field.email')}
              </label>
              <input id="email" type="email" className="input-base" autoComplete="email" {...register('email')} />
              {errors.email && <p className="mt-1 text-xs text-custel-danger">{errors.email.message}</p>}
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-custel-text" htmlFor="password">
                {t('signup.field.password')}{' '}
                <span className="text-custel-text-tertiary">{t('signup.field.passwordHint')}</span>
              </label>
              <input
                id="password"
                type="password"
                className="input-base"
                autoComplete="new-password"
                {...register('password')}
              />
              {errors.password && <p className="mt-1 text-xs text-custel-danger">{errors.password.message}</p>}
            </div>
          </fieldset>

          <button type="submit" className="btn-primary w-full" disabled={submitting}>
            {submitting ? t('signup.submitting') : t('signup.submit')}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-custel-text-secondary">
          {t('signup.haveAccount')}{' '}
          <Link to="/giris" className="font-medium text-custel-ink underline underline-offset-2 hover:text-custel-g700">
            {t('signup.loginLink')}
          </Link>
        </p>
      </div>
    </main>
  );
}
